import {
  Area,
  AreaChart,
  Bar,
  ComposedChart,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

type IndicatorVisualProps = {
  id: 'ma' | 'rsi' | 'macd';
  series: number[];
};

const TEAL = 'oklch(var(--primary-500))';
const TEAL_SOFT = 'oklch(var(--primary-200))';
const ORANGE = 'oklch(var(--accent-500))';
const ORANGE_SOFT = 'oklch(var(--accent-200))';
const GRID = 'oklch(var(--background-300))';

function toChartData(series: number[]) {
  return series.map((value, index) => ({ index, value }));
}

/** A simple lagged average used only to draw a signal line for the teaching figure. */
function signalLine(series: number[]) {
  return series.map((_, index) => {
    const window = series.slice(Math.max(0, index - 2), index + 1);
    return window.reduce((sum, item) => sum + item, 0) / window.length;
  });
}

export default function IndicatorVisual({ id, series }: IndicatorVisualProps) {
  if (id === 'rsi') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={toChartData(series)} margin={{ top: 6, right: 6, bottom: 6, left: 6 }}>
          <XAxis dataKey="index" hide />
          <YAxis domain={[0, 100]} hide />
          <ReferenceArea y1={70} y2={100} fill={ORANGE_SOFT} fillOpacity={0.45} />
          <ReferenceArea y1={0} y2={30} fill={TEAL_SOFT} fillOpacity={0.45} />
          <ReferenceLine y={70} stroke={ORANGE} strokeDasharray="3 3" strokeWidth={1} />
          <ReferenceLine y={30} stroke={TEAL} strokeDasharray="3 3" strokeWidth={1} />
          <ReferenceLine y={50} stroke={GRID} strokeWidth={1} />
          <Line type="monotone" dataKey="value" stroke={TEAL} strokeWidth={2.2} dot={false} isAnimationActive />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (id === 'macd') {
    const signal = signalLine(series);
    const data = series.map((value, index) => ({
      index,
      macd: value,
      signal: signal[index],
      positive: value > 0 ? value : 0,
      negative: value < 0 ? value : 0,
    }));

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 6, right: 6, bottom: 6, left: 6 }}>
          <XAxis dataKey="index" hide />
          <YAxis hide />
          <ReferenceLine y={0} stroke={GRID} strokeWidth={1} />
          <Bar dataKey="positive" fill={TEAL_SOFT} barSize={7} isAnimationActive radius={[1, 1, 0, 0]} />
          <Bar dataKey="negative" fill={ORANGE_SOFT} barSize={7} isAnimationActive radius={[0, 0, 1, 1]} />
          <Line type="monotone" dataKey="macd" stroke={TEAL} strokeWidth={2} dot={false} isAnimationActive />
          <Line type="monotone" dataKey="signal" stroke={ORANGE} strokeWidth={1.6} strokeDasharray="4 3" dot={false} isAnimationActive />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={toChartData(series)} margin={{ top: 8, right: 6, bottom: 6, left: 6 }}>
        <XAxis dataKey="index" hide />
        <YAxis hide domain={['dataMin - 2', 'dataMax + 2']} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={TEAL}
          strokeWidth={2.4}
          fill={TEAL_SOFT}
          fillOpacity={0.35}
          dot={false}
          isAnimationActive
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}